import { useAtomValue } from "jotai";
import { Suspense } from "react";
import { Outlet } from "react-router-dom";
import { type VariantProps } from "tailwind-variants";

import { Spinner } from "@/components/atoms/Spinner/Spinner";
import { MainHeader } from "@/components/organisms/Header";
import { sidebarCollapsedAtom } from "@/stores/layout";

import {
  CONTENT_PADDING,
  CONTENT_SCROLL_TYPE,
  mainLayoutVariants,
} from "./main-layout-variants";
import { Sidebar } from "./Sidebar";

type MainLayoutVariants = VariantProps<typeof mainLayoutVariants>;
interface MainLayoutProps extends MainLayoutVariants {
  isExpand?: boolean;
  contentScrollType?: (typeof CONTENT_SCROLL_TYPE)[keyof typeof CONTENT_SCROLL_TYPE];
  contentPadding?: (typeof CONTENT_PADDING)[keyof typeof CONTENT_PADDING];
  headerStatusSlot?: React.ReactNode;
}

const SuspenseFallback = () => {
  return (
    <div className="grid h-dvh place-content-center">
      <Spinner className="size-5 text-primary" />
      <span className="sr-only">読み込み中…</span>
    </div>
  );
};

export const MainLayout = ({
  isExpand = false,
  contentScrollType = CONTENT_SCROLL_TYPE.PAGE,
  contentPadding = CONTENT_PADDING.DEFAULT,
  headerStatusSlot,
}: MainLayoutProps) => {
  const { main } = mainLayoutVariants({
    isExpand,
    contentScrollType,
    contentPadding,
  });
  const sidebarCollapsed = useAtomValue(sidebarCollapsedAtom);

  return (
    <div
      className={`grid bg-gray-50 transition-all duration-200 ${sidebarCollapsed ? "grid-cols-[60px_minmax(0,1fr)]" : "grid-cols-[213px_minmax(0,1fr)]"}`}
    >
      <div className="">
        <Sidebar />
      </div>
      <div className="">
        <MainHeader headerStatusSlot={headerStatusSlot} />
        <main className={main()}>
          <Suspense fallback={<SuspenseFallback />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
};
